/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation, useNavigate, useRoutes } from 'react-router-dom'
import { DateTime } from 'luxon'

/**********************************************************************************************************
 *   STATE IMPORTS
 **********************************************************************************************************/
import { useAppSelector } from 'store/hooks'
import { Fade } from '@nexigendigital/nxui'

/**********************************************************************************************************
 *   STYLE IMPORTS
 **********************************************************************************************************/
import { Authentication } from './login.styles'

/**********************************************************************************************************
 *   FORM IMPORTS
 **********************************************************************************************************/
import LoginForm from 'containers/login/forms/authenticate.form'
import AccountForm from 'containers/login/forms/accounts.form'
import ForgotForm from 'containers/login/forms/forgot.form'
import TwoFactorForm from 'containers/login/forms/twoFactor.form'
import TwoFactorRecovery from 'containers/login/forms/recovery.form'
import ResetPassword from 'containers/login/forms/reset.form'

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function Login() {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const location = useLocation()
    const {
        appActiveRouter: { meta: { loading, message } },
        appSession: { authenticated, two_factor_required },
        appSettings: { provider: { application_name, provider_name, business_number, url, logo_url } },
        appAuthentication: { twofactor: { primary: twoFactorPrimary } }
    } = useAppSelector((state) => state.app)

    /*   STATE
    *****************************************************/
    const [checkedSession, setCheckedSession] = useState(false)

    /*   EFFECTS
     *****************************************************/
    useEffect(() => {
        if (!checkedSession) {
            two_factor_required && navigate('/login/two-factor')
            !two_factor_required && authenticated && navigate('/login/accounts')

            setCheckedSession(true)
        }
    }, [dispatch, navigate, authenticated, two_factor_required, checkedSession])

    /*   FUNCTIONS
    *****************************************************/
    function renderTitle() {
        switch (true) {
            case location.pathname.includes('/accounts'):
                return (
                    <Authentication.Subtitle>
                        <h2>Select an Account</h2>
                        <p>Select one of the following user accounts to continue.</p>
                    </Authentication.Subtitle>
                )

            case location.pathname.includes('/reset-password'):
                return (
                    <Authentication.Subtitle>
                        <h2>Reset Account Password</h2>
                        <p>Fill out the form below to complete your password reset request.</p>
                    </Authentication.Subtitle>
                )

            case location.pathname.includes('/forgot'):
                return ''

            case location.pathname.includes('/two-factor'):
                return (
                    <Authentication.Subtitle>
                        <h2>Two-Factor Authentication</h2>
                        <p>{renderTwoFactorDescription()}</p>
                    </Authentication.Subtitle>
                )

            case location.pathname.includes('/account-recovery'):
                return (
                    <Authentication.Subtitle>
                        <h2>Two-Factor Options</h2>
                        <p>If you&apos;re having trouble with two-factor authentication, here are some alternatives.</p>
                    </Authentication.Subtitle>
                )

            default:
                return ('')
        }
    }

    function renderTwoFactorDescription() {
        loading && <></>
        switch (twoFactorPrimary?.type) {
            case 'authenticator':
                return 'Enter the two-factor authentication code which is available via your authenticator app.'
            case 'sms':
                return `Enter the two-factor authentication code which was sent via SMS to ${twoFactorPrimary!.identifier}.`
            case 'email':
                return `Enter the two-factor authentication code which was sent to ${twoFactorPrimary!.identifier}.`
            default:
                return ''
        }
    }

    /*   RENDER COMPONENT
     *****************************************************/
    return (
        <Authentication.Base>
            <Authentication.Wrapper>
                <Authentication.Company>
                    <Authentication.Logo>
                        <img width={'300'} src={logo_url} alt={application_name} />
                    </Authentication.Logo>
                    <Authentication.Title>{application_name}</Authentication.Title>
                </Authentication.Company>
                <Authentication.Form>
                    <Fade when={loading || location.key}>
                        <Authentication.Transition>
                            {renderTitle()}
                            {loading
                                ? <Authentication.Loading message={message} />
                                : useRoutes([
                                    { path: 'reset-password/:token', element: <ResetPassword /> },
                                    { path: 'two-factor', element: <TwoFactorForm /> },
                                    { path: 'account-recovery', element: <TwoFactorRecovery /> },
                                    { path: 'forgot', element: <ForgotForm /> },
                                    { path: 'accounts', element: <AccountForm /> },
                                    { path: '/', element: <LoginForm /> }
                                ])
                            }
                        </Authentication.Transition>
                    </Fade>
                </Authentication.Form>
                <Authentication.Ext />
            </Authentication.Wrapper>
            <Authentication.Footer>
                <span>&copy; {DateTime.local().year} - {provider_name} - ABN {business_number}</span>
                <Authentication.Anchor href={url} target={'_blank'}>Website</Authentication.Anchor>
            </Authentication.Footer>
        </Authentication.Base>
    )
}
